import { watch } from 'vue';
import { toValue } from '@vueuse/core';
import addItemToBag from '@common-vue/add-to-bag';
import { useMutation } from '@composables/utils/useMutation';
import { ExpressCheckoutService } from '../services/ExpressCheckout.service';
import { ATBError, ATBValidationError } from '../errors';
import { tagATBValidationError } from '../features/analytics/ATBValidationError';

const ADD_TO_BAG_MUTATION_KEY = Symbol('Add To Bag Mutation');
/**
 * * Composable that handles the Add To Bag (ATB) functionality
 * Main purpose of this composable is to provide a consistent component API for the ATB functionality,
 * and to handle the ATB errors in a consistent way.
 *
 * @description
 * This composable is a wrapper around the ATB store module.
 * It provides a function that triggers the ATB action and returns the bag object.
 * It also provides a color and size validation.
 *
 * @see {@link https://confluence.federated.fds/display/CAPPD/Vue+Add+to+Bag+flow+with+component%27s+technical+details}
 *
 * @param {Object} payload - The product id.
 * @param {import('@vueuse/shared').MaybeRefOrGetter<ATBPayload>} [payload.payload] - The product id.
 * @param {ATBPayloadValidator} [payload.payloadValidator] - payload validation function
 * @param {ATBFlags} flags - ATB flags
 * @param {import('@composables/utils/useMutation').MutationCallbacks<any>} [callbacks] - The callbacks object.
 * The callback function that will be called after the ATB action is triggered.
 */
export function useAddToBag(
  {
    payload,
    payloadValidator = () => null,
  } = {},
  { checkoutRedirect = false } = {},
  callbacks,
) {
  if (!payload) {
    throw new ATBError('payload is required');
  }

  const { isMcom = true } = toValue(payload)?.flags || {};
  const expressCheckoutService = new ExpressCheckoutService();

  /**
   * * Adds Payload to bag (with validation)
   *
   * @description
   * If the validation fails, an error will be thrown.
   * And the main advantage of this composable is to provide a consistent way to handle the ATB errors.
   * That's why we need to validate the payload on our own.
   */
  const addToBag = async ({ isUpdate = false, buynow = false } = {}) => {
    const payloadValue = toValue(payload);
    const errorMessage = await payloadValidator(payloadValue);
    if (errorMessage) {
      throw new ATBValidationError(errorMessage);
    }

    if (!isUpdate) {
      delete payloadValue?.options?.sequenceNumber;
    }

    // TODO merge payload
    if (payloadValue?.options?.sourceUtagData) {
      payloadValue.options.sourceUtagData.buyNow = buynow;
    }

    try {
      const result = await addItemToBag(payloadValue.data, {
        useModal: isMcom,
        isUpdate,
        ...payloadValue.options,
      });
      checkoutRedirect && await expressCheckoutService.checkout(); // eslint-disable-line no-unused-expressions
      return result;
    } catch (e) {
      if (e instanceof Error) {
        throw new ATBError(e.message);
      }
      console.error(e); // eslint-disable-line no-console
      throw new Error('Unknown Error');
    }
  };

  const addToBagMutation = useMutation(addToBag, {
    key: ADD_TO_BAG_MUTATION_KEY,
    resetOnPageChange: true,
    /**
     * @param {any} data
     */
    ...callbacks,
    onError(error) {
      callbacks?.onError?.(error);
      if (error instanceof ATBValidationError) {
        tagATBValidationError(error?.message);
      }
    },
  });

  watch(addToBagMutation.isLoading, (newValue) => {
    if (!newValue) return;
    addToBagMutation.data.value = null;
    addToBagMutation.resetError();
  });

  watch(() => toValue(payload), addToBagMutation.resetError);

  return addToBagMutation;
}

export default useAddToBag;
